/**
 * Shared multi-agent string + run-status helpers.
 *
 * Centralizes label-prettifying, the run terminal/in-flight predicates, and the
 * character-class checks used by the various lightweight parsers, so they aren't
 * re-derived per file.
 */

export type RunStatus =
  | "idle"
  | "starting"
  | "planning"
  | "awaiting_approval"
  | "running"
  | "done"
  | "failed"
  | "cancelled";

/** Run states after which no more streaming/work happens. */
export const RUN_TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set([
  "done",
  "failed",
  "cancelled",
]);

/** Run states where a run is actively in progress (a new send must be blocked). */
export const RUN_IN_FLIGHT_STATUSES: ReadonlySet<RunStatus> = new Set([
  "starting",
  "planning",
  "awaiting_approval",
  "running",
]);

export function isTerminalRunStatus(status: string | null | undefined): boolean {
  return !!status && RUN_TERMINAL_STATUSES.has(status as RunStatus);
}

export function isRunInFlight(status: string | null | undefined): boolean {
  return !!status && RUN_IN_FLIGHT_STATUSES.has(status as RunStatus);
}

// ── Character-class checks ───────────────────────────────────────────────────
export const isWhitespace = (c: string): boolean =>
  c === " " || c === "\t" || c === "\n" || c === "\r" || c === "\f" || c === "\v";

export const isAlphanumeric = (c: string): boolean =>
  (c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || (c >= "0" && c <= "9");

export const isWordChar = (c: string): boolean => isAlphanumeric(c) || c === "_";

export const isHexDigit = (c: string): boolean =>
  (c >= "0" && c <= "9") || (c >= "a" && c <= "f") || (c >= "A" && c <= "F");

/**
 * Turn a snake_case status/enum into a human label ("awaiting_approval" →
 * "awaiting approval").
 */
export function humanizeStatus(value: string | null | undefined): string {
  return (value || "").split("_").join(" ");
}

/** Map raw tool names → readable phrases for the timeline and rail. */
const TOOL_LABELS: Record<string, string> = {
  apex_blast_radius: "Blast radius",
  query_org: "Query org",
  get_apex: "Get Apex class",
  get_flow: "Get flow",
  get_object: "Get object schema",
  search_apex: "Search Apex",
  search_metadata: "Search metadata",
  list_objects: "List objects",
  run_soql: "Run SOQL",
  run_tooling: "Run tooling query",
  get_permissions: "Get permissions",
  get_profiles: "Get profiles",
  analyze_dependencies: "Analyze dependencies",
  plan: "Plan",
  replan: "Replan",
  delegate: "Delegate task",
  summarize: "Summarize",
  propose_fix: "Propose fix",
  apply_fix: "Apply fix",
  validate_fix: "Validate fix",
  auto_fix_propose: "Auto-fix propose",
  web_search: "Web search",
  read_file: "Read file",
  write_file: "Write file",
  list_files: "List files",
  code_execute: "Execute code",
  compress_context: "Compress context",
};

export function humanizeTool(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.toLowerCase().replace(/-/g, "_");
  if (TOOL_LABELS[key]) return TOOL_LABELS[key];
  return raw.split(/[_.-]/).map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : "")).join(" ");
}

/** Collapse runs of whitespace into single spaces and trim. */
export function collapseWhitespace(value: string | null | undefined): string {
  const out: string[] = [];
  let pendingSpace = false;
  for (const ch of value || "") {
    if (isWhitespace(ch)) {
      if (out.length > 0) pendingSpace = true;
      continue;
    }
    if (pendingSpace) {
      out.push(" ");
      pendingSpace = false;
    }
    out.push(ch);
  }
  return out.join("");
}
